import { CheckOutlined, CloseOutlined, ExclamationOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import React from 'react';

export type InspectionStatus = 'Success' | 'Warning' | 'Failure';

interface IOption {
  value: InspectionStatus;
  icon: React.ReactElement;
  colour: string;
  tint: string;
  label: string;
}

export const INSPECTION_STATUS_OPTIONS: IOption[] = [
  { value: 'Success', icon: <CheckOutlined />, colour: '#52c41a', tint: '#f6ffed', label: 'Pass' },
  {
    value: 'Warning',
    icon: <ExclamationOutlined />,
    colour: '#faad14',
    tint: '#fffbe6',
    label: 'Advisory',
  },
  { value: 'Failure', icon: <CloseOutlined />, colour: '#ff4d4f', tint: '#fff1f0', label: 'Fail' },
];

export const inspectionStatusColour = (status?: string | null) =>
  INSPECTION_STATUS_OPTIONS.find((option) => option.value === status)?.colour;

interface IProps {
  value?: InspectionStatus | null;
  /** Called with the status itself, or null when the selected one is pressed again to unset it. */
  onChange?: (value: InspectionStatus | null) => void;
  disabled?: boolean;
}

/**
 * Three buttons, sized to sit on the same line as the item they answer.
 *
 * Plain buttons rather than a Radio.Group: a checklist renders this forty-odd times, and the group
 * needed a stylesheet injected per instance to be talked out of its own borders. Pressing the active
 * one again clears it, which is what the separate Clear link used to be for.
 */
export const StatusRadioGroup: React.FC<IProps> = ({ value, onChange, disabled }) => (
  <div style={{ display: 'flex', gap: 6 }}>
    {INSPECTION_STATUS_OPTIONS.map((option) => {
      const isActive = value === option.value;

      return (
        <Tooltip key={option.value} title={option.label} mouseEnterDelay={0.6}>
          <button
            type="button"
            disabled={disabled}
            aria-label={option.label}
            aria-pressed={isActive}
            onClick={() => onChange?.(isActive ? null : option.value)}
            style={{
              width: 40,
              height: 32,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 16,
              lineHeight: 1,
              cursor: disabled ? 'not-allowed' : 'pointer',
              borderRadius: 8,
              border: `1px solid ${isActive ? option.colour : '#e6e6e6'}`,
              background: isActive ? option.colour : option.tint,
              color: isActive ? '#fff' : option.colour,
              transition: 'background 0.15s, border-color 0.15s',
            }}
          >
            {option.icon}
          </button>
        </Tooltip>
      );
    })}
  </div>
);
